import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPaymentLinkById, verifyPaymentLinkStatus, clearPaymentLink } from '../../actions/paymentLink';
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Badge,
    Alert,
    Spinner,
    Table
} from 'react-bootstrap';
import {
    ArrowLeft,
    Link2,
    RefreshCw,
    CheckCircle,
    User,
    Calendar,
    IndianRupee,
    Copy,
    ExternalLink,
    Package
} from 'lucide-react';

const PaymentLinkDetail = ({
    paymentLink,
    loading,
    error,
    getPaymentLinkById,
    verifyPaymentLinkStatus,
    clearPaymentLink
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        document.title = 'Payment Link Details';
        if (id) {
            getPaymentLinkById(id);
        }
        return () => {
            clearPaymentLink();
        };
    }, [getPaymentLinkById, clearPaymentLink, id]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            created: { variant: 'info', label: 'Created' },
            issued: { variant: 'primary', label: 'Issued' },
            paid: { variant: 'success', label: 'Paid' },
            partially_paid: { variant: 'warning', label: 'Partially Paid' },
            cancelled: { variant: 'danger', label: 'Cancelled' },
            expired: { variant: 'secondary', label: 'Expired' }
        };
        const config = statusConfig[status?.toLowerCase()] || { variant: 'secondary', label: status || 'Unknown' };
        return <Badge bg={config.variant} className="fs-6">{config.label}</Badge>;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleVerifyStatus = async () => {
        setVerifying(true);
        try {
            await verifyPaymentLinkStatus(id);
        } catch (err) {
            console.error('Error verifying payment link:', err);
        } finally {
            setVerifying(false);
        }
    };

    if (loading && !paymentLink) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading payment link details...</p>
                </div>
            </Container>
        );
    }

    if (error && !paymentLink) {
        return (
            <Container className="py-4">
                <Alert variant="danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                    <ArrowLeft className="me-2" size={16} />
                    Go Back
                </Button>
            </Container>
        );
    }

    if (!paymentLink) {
        return (
            <Container className="py-4">
                <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    Payment link not found.
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(-1)}>
                    <ArrowLeft className="me-2" size={16} />
                    Go Back
                </Button>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button
                        variant="outline-primary"
                        as={Link}
                        to="/inventory/v1.2/payment-links"
                        className="mb-2"
                    >
                        <ArrowLeft className="me-2" size={16} />
                        Back to Payment Links
                    </Button>
                    <h2 className="text-primary mb-0">
                        <Link2 className="me-2" size={28} />
                        Payment Link Details
                    </h2>
                </div>
                <div className="d-flex gap-2">
                    <Button
                        variant="outline-success"
                        onClick={handleVerifyStatus}
                        disabled={verifying || loading}
                    >
                        <RefreshCw className="me-2" size={16} />
                        {verifying ? 'Verifying...' : 'Verify Status'}
                    </Button>
                </div>
            </div>

            <Row className="g-4">
                {/* Payment Link Information */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">
                                <Link2 className="me-2" size={18} />
                                Payment Link Information
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Payment Link ID:</td>
                                        <td className="fw-bold text-info">{paymentLink.paymentLink || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Status:</td>
                                        <td>{getStatusBadge(paymentLink.status)}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Payment Link URL:</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <a
                                                    href={paymentLink.paymentLinkUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none small"
                                                >
                                                    {paymentLink.paymentLinkUrl}
                                                </a>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => copyToClipboard(paymentLink.paymentLinkUrl)}
                                                    title="Copy link"
                                                >
                                                    {copied ? (
                                                        <CheckCircle size={14} />
                                                    ) : (
                                                        <Copy size={14} />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Amount:</td>
                                        <td>
                                            <span className="fw-bold text-success">
                                                <IndianRupee size={14} className="me-1" />
                                                {paymentLink.amount?.toLocaleString() || '0.00'}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Amount Paid:</td>
                                        <td>
                                            <span className="fw-bold text-info">
                                                <IndianRupee size={14} className="me-1" />
                                                {paymentLink.amountPaid?.toLocaleString() || '0.00'}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Currency:</td>
                                        <td>{paymentLink.currency || 'INR'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Created At:</td>
                                        <td>
                                            <Calendar className="me-1" size={14} />
                                            {formatDate(paymentLink.createdAt)}
                                        </td>
                                    </tr>
                                    {paymentLink.paidAt && (
                                        <tr>
                                            <td className="fw-bold text-muted">Paid At:</td>
                                            <td>
                                                <Calendar className="me-1" size={14} />
                                                {formatDate(paymentLink.paidAt)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Order Information */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-success text-white">
                            <h5 className="mb-0">
                                <Package className="me-2" size={18} />
                                Order Information
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Order ID:</td>
                                        <td>
                                            <Link
                                                to={`/inventory/v1.2/orders/${paymentLink.orderId?._id}`}
                                                className="text-decoration-none fw-bold text-primary"
                                            >
                                                {paymentLink.orderId?.orderId || paymentLink.orderId?._id || 'N/A'}
                                            </Link>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Reference ID:</td>
                                        <td className="font-monospace small">{paymentLink.referenceId || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Description:</td>
                                        <td>{paymentLink.description || 'N/A'}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Customer Information */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-info text-white">
                            <h5 className="mb-0">
                                <User className="me-2" size={18} />
                                Customer Information
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Name:</td>
                                        <td className="fw-bold">{paymentLink.customer?.name || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Email:</td>
                                        <td>{paymentLink.customer?.email || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-bold text-muted">Phone:</td>
                                        <td>{paymentLink.customer?.phone || 'N/A'}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Payment Settings */}
                <Col lg={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-warning text-dark">
                            <h5 className="mb-0">
                                <i className="fas fa-cog me-2"></i>
                                Payment Settings
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Table borderless className="mb-0">
                                <tbody>
                                    <tr>
                                        <td className="fw-bold text-muted">Accept Partial:</td>
                                        <td>
                                            <Badge bg={paymentLink.acceptPartial ? 'success' : 'secondary'}>
                                                {paymentLink.acceptPartial ? 'Yes' : 'No'}
                                            </Badge>
                                        </td>
                                    </tr>
                                    {paymentLink.acceptPartial && paymentLink.firstMinPartialAmount && (
                                        <tr>
                                            <td className="fw-bold text-muted">Min Partial Amount:</td>
                                            <td>{paymentLink.firstMinPartialAmount}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td className="fw-bold text-muted">Reminder Enabled:</td>
                                        <td>
                                            <Badge bg={paymentLink.reminderEnable ? 'success' : 'secondary'}>
                                                {paymentLink.reminderEnable ? 'Yes' : 'No'}
                                            </Badge>
                                        </td>
                                    </tr>
                                    {paymentLink.expireBy && (
                                        <tr>
                                            <td className="fw-bold text-muted">Expires At:</td>
                                            <td>
                                                <Calendar className="me-1" size={14} />
                                                {formatDate(paymentLink.expiresAt || paymentLink.expireBy)}
                                            </td>
                                        </tr>
                                    )}
                                    {paymentLink.callbackUrl && (
                                        <tr>
                                            <td className="fw-bold text-muted">Callback URL:</td>
                                            <td className="small">{paymentLink.callbackUrl}</td>
                                        </tr>
                                    )}
                                    {paymentLink.callbackMethod && (
                                        <tr>
                                            <td className="fw-bold text-muted">Callback Method:</td>
                                            <td className="text-uppercase">{paymentLink.callbackMethod}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Payments */}
                {paymentLink.payments && paymentLink.payments.length > 0 && (
                    <Col lg={12}>
                        <Card>
                            <Card.Header className="bg-success text-white">
                                <h5 className="mb-0">
                                    <CheckCircle className="me-2" size={18} />
                                    Payment Transactions
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Table striped bordered hover responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Payment ID</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Method</th>
                                            <th>Created At</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentLink.payments.map((payment, index) => (
                                            <tr key={payment._id || index}>
                                                <td className="font-monospace small">{payment.paymentId}</td>
                                                <td>
                                                    <span className="fw-bold text-success">
                                                        <IndianRupee size={14} className="me-1" />
                                                        {(payment.amount / 100).toFixed(2)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <Badge bg={payment.status === 'captured' ? 'success' : payment.status === 'failed' ? 'danger' : 'warning'}>
                                                        {payment.status}
                                                    </Badge>
                                                </td>
                                                <td className="text-capitalize">{payment.method}</td>
                                                <td>
                                                    <Calendar className="me-1" size={14} />
                                                    {formatDate(payment.createdAt)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                {/* Additional Notes */}
                {paymentLink.notes && Object.keys(paymentLink.notes).length > 0 && (
                    <Col lg={12}>
                        <Card>
                            <Card.Header className="bg-secondary text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-sticky-note me-2"></i>
                                    Additional Notes
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Table bordered className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Key</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(paymentLink.notes).map(([key, value]) => (
                                            <tr key={key}>
                                                <td className="fw-bold">{key}</td>
                                                <td>{value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                {/* Razorpay Data (if available from verify) */}
                {paymentLink.razorpayData && (
                    <Col lg={12}>
                        <Card>
                            <Card.Header className="bg-dark text-white">
                                <h5 className="mb-0">
                                    <ExternalLink className="me-2" size={18} />
                                    Razorpay Information
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Table borderless>
                                            <tbody>
                                                <tr>
                                                    <td className="fw-bold text-muted">Razorpay ID:</td>
                                                    <td className="font-monospace small">{paymentLink.razorpayData.id}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Razorpay Status:</td>
                                                    <td>{getStatusBadge(paymentLink.razorpayData.status)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Amount (Paise):</td>
                                                    <td>{paymentLink.razorpayData.amount}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Amount Paid (Paise):</td>
                                                    <td>{paymentLink.razorpayData.amount_paid}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                    <Col md={6}>
                                        <Table borderless>
                                            <tbody>
                                                <tr>
                                                    <td className="fw-bold text-muted">Razorpay Order ID:</td>
                                                    <td className="font-monospace small">{paymentLink.razorpayData.order_id || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Created At:</td>
                                                    <td>{formatDate(paymentLink.razorpayCreatedAt)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-bold text-muted">Updated At:</td>
                                                    <td>{formatDate(paymentLink.razorpayUpdatedAt)}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>

                                {/* Payments Array */}
                                {paymentLink.razorpayData.payments && paymentLink.razorpayData.payments.length > 0 && (
                                    <div className="mt-3">
                                        <h6 className="text-muted">Payments:</h6>
                                        <Table striped bordered className="mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Payment ID</th>
                                                    <th>Amount (Paise)</th>
                                                    <th>Status</th>
                                                    <th>Method</th>
                                                    <th>Created At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paymentLink.razorpayData.payments.map((payment, index) => (
                                                    <tr key={index}>
                                                        <td className="font-monospace small">{payment.payment_id}</td>
                                                        <td>{(payment.amount / 100).toFixed(2)}</td>
                                                        <td>
                                                            <Badge bg={payment.status === 'captured' ? 'success' : 'warning'}>
                                                                {payment.status}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-capitalize">{payment.method}</td>
                                                        <td>{new Date(payment.created_at * 1000).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
        </Container>
    );
};

PaymentLinkDetail.propTypes = {
    paymentLink: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    getPaymentLinkById: PropTypes.func.isRequired,
    verifyPaymentLinkStatus: PropTypes.func.isRequired,
    clearPaymentLink: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    paymentLink: state.paymentLink.paymentLink,
    loading: state.paymentLink.loading,
    error: state.paymentLink.error
});

export default connect(mapStateToProps, {
    getPaymentLinkById,
    verifyPaymentLinkStatus,
    clearPaymentLink
})(PaymentLinkDetail);

